import React, { useState, useMemo } from 'react'; // Ajout de useMemo
import PropTypes from 'prop-types';
import {
  Button,
  Table,
  Card,
  Row,
  Col,
  Tabs, // Importer Tabs
  Tab, // Importer Tab
  Form // Importer Form pour le champ de recherche
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExchangeAlt,
  faSpinner,
  faUndo,
  faUserEdit,
  faSearch // Pour l'icône de recherche
} from '@fortawesome/free-solid-svg-icons';
import ReplaceParticipantModal from './ReplaceParticipantModal';
import api from 'services/api';
import { toast } from 'react-toastify';

const formatParticipantName = p => {
  if (!p) return 'N/A';
  let mainName =
    `${p.firstname || ''} ${p.lastname || ''}`.trim() || 'Participant Inconnu';
  if (p.is_replaced) {
    const replacementName = `${p.replacement_firstname || ''} ${
      p.replacement_lastname || ''
    }`.trim();
    return (
      <span>
        <span style={{ textDecoration: 'line-through' }}>{mainName}</span>{' '}
        <FontAwesomeIcon
          icon={faExchangeAlt}
          className="text-muted"
          title={`Remplacé par ${replacementName || 'N/A'}`}
        />{' '}
        <span className="text-info">{replacementName || 'N/A'}</span>
      </span>
    );
  }
  return mainName;
};

const ReplacementTab = ({
  salon,
  onParticipantReplaced,
  dynamicTabContentContainerStyle,
  isFullScreen
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantTypeForModal, setParticipantTypeForModal] = useState('');
  const [cancellingParticipantId, setCancellingParticipantId] = useState(null);
  const [activeTab, setActiveTab] = useState('company'); // 'company' ou 'partner'
  const [searchTerm, setSearchTerm] = useState('');

  const handleOpenModal = (participant, type) => {
    setSelectedParticipant(participant);
    setParticipantTypeForModal(type);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedParticipant(null);
    setParticipantTypeForModal('');
  };

  const handleReplacementSuccessFromModal = updatedMeetingsDataFromApi => {
    onParticipantReplaced(updatedMeetingsDataFromApi);
    handleModalClose();
  };

  const handleCancelReplacement = async (participantToRestore, type) => {
    if (!participantToRestore || !participantToRestore.id) return;
    if (
      !window.confirm(
        `Voulez-vous vraiment annuler le remplacement de ${participantToRestore.firstname} ${participantToRestore.lastname} et restaurer ce participant ?`
      )
    ) {
      return;
    }
    setCancellingParticipantId(participantToRestore.id);
    try {
      const response = await api.post(
        `/admin/partner-salons/${salon.id}/participants/${type}/${participantToRestore.id}/cancel-replacement`
      );
      if (response.data && response.data.success) {
        onParticipantReplaced(response.data.data);
        toast(
          response.data.message ||
            `Le remplacement de ${participantToRestore.firstname} ${participantToRestore.lastname} a été annulé.`
        );
      } else {
        toast(
          response.data?.message ||
            "Erreur lors de l'annulation du remplacement (réponse inattendue)."
        );
      }
    } catch (error) {
      console.error("Erreur lors de l'annulation du remplacement:", error);
      toast(
        error.response?.data?.message ||
          "Une erreur est survenue lors de l'annulation du remplacement."
      );
    } finally {
      setCancellingParticipantId(null);
    }
  };

  const handleMarkAsAbsent = async (participant, type) => {
    if (!participant || !participant.id) return;

    const confirmMessage = `Si vous marquez cette personne comme absente le jour même du salon, un mail sera envoyé à toutes les personnes qu'elle est censée rencontrer. Voulez-vous continuer ?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await api.post(
        `/admin/partner-salons/${salon.id}/participants/${type}/${participant.id}/mark-absent`
      );

      if (response.data && response.data.success) {
        onParticipantReplaced(response.data.data); // Mettre à jour les données après l'absence
        toast(
          response.data.message || `Cette personne a été marquée comme absente.`
        );
      } else {
        toast(
          response.data?.message ||
            "Erreur lors de la mise à jour de l'absence (réponse inattendue)."
        );
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'absence :", error);
      toast(
        error.response?.data?.message ||
          "Une erreur est survenue lors de la mise à jour de l'absence."
      );
    }
  };

  const filteredCompanyParticipants = useMemo(() => {
    if (!searchTerm) return salon.company_participants || [];
    return (salon.company_participants || []).filter(p =>
      `${p.firstname || ''} ${p.lastname || ''} ${
        p.replacement_firstname || ''
      } ${p.replacement_lastname || ''} ${p.company?.name || ''}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  }, [salon.company_participants, searchTerm]);

  const filteredPartnerParticipants = useMemo(() => {
    if (!searchTerm) return salon.partner_participants || [];
    return (salon.partner_participants || []).filter(p =>
      `${p.firstname || ''} ${p.lastname || ''} ${
        p.replacement_firstname || ''
      } ${p.replacement_lastname || ''} ${p.partner?.name || ''}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  }, [salon.partner_participants, searchTerm]);

  const renderParticipantsTable = (participants, type, typeLabel) => {
    if (!participants || participants.length === 0) {
      return (
        <Card.Body>
          <p className="text-muted mt-3">
            {searchTerm
              ? `Aucun participant ${typeLabel.toLowerCase()} ne correspond à votre recherche.`
              : `Aucun participant ${typeLabel.toLowerCase()} pour ce salon.`}
          </p>
        </Card.Body>
      );
    }

    return (
      <div
        style={dynamicTabContentContainerStyle(isFullScreen)}
        className="mt-0"
      >
        <Table
          striped
          bordered
          hover
          responsive
          size="sm"
          className="fs--1 mb-0"
        >
          <thead className="bg-200 text-900">
            <tr>
              <th>Nom</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Statut</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {participants.map(p => (
              <tr key={`${type}-${p.id}`}>
                <td>
                  <strong>{formatParticipantName(p)}</strong>
                </td>
                <td>
                  {p.is_replaced
                    ? p.replacement_email || 'N/A'
                    : p.email || 'N/A'}
                </td>
                <td>
                  {p.is_replaced
                    ? p.replacement_phone || 'N/A'
                    : p.phone || 'N/A'}
                </td>
                <td>
                  {p.is_absent ? (
                    <span className="text-danger">Absent</span>
                  ) : p.is_replaced ? (
                    <span className="text-info">
                      Remplacé par {p.replacement_firstname}{' '}
                      {p.replacement_lastname}
                    </span>
                  ) : (
                    <span className="text-success">Actif</span>
                  )}
                </td>
                <td>
                  {!p.is_absent && (
                    <>
                      {p.is_replaced ? (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => handleCancelReplacement(p, type)}
                          disabled={cancellingParticipantId === p.id}
                          title="Annuler le remplacement et restaurer ce participant"
                        >
                          {cancellingParticipantId === p.id ? (
                            <FontAwesomeIcon
                              icon={faSpinner}
                              spin
                              className="me-1"
                            />
                          ) : (
                            <FontAwesomeIcon icon={faUndo} className="me-1" />
                          )}
                          Annuler
                        </Button>
                      ) : (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleOpenModal(p, type)}
                          title="Remplacer ce participant"
                        >
                          <FontAwesomeIcon icon={faUserEdit} className="me-1" />{' '}
                          Remplacer
                        </Button>
                      )}

                      <Button
                        className="ms-2"
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleMarkAsAbsent(p, type)}
                        title="Marquer comme absent"
                      >
                        <FontAwesomeIcon icon={faUserEdit} className="me-1" />{' '}
                        Absent
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <div className="mt-3">
      <Row className="mb-3">
        <Col>
          <Form.Group controlId="participantSearch">
            <Form.Label className="visually-hidden">
              Rechercher un participant
            </Form.Label>
            <div className="input-group">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faSearch} />
              </span>
              <Form.Control
                type="text"
                placeholder="Rechercher par nom, prénom, nom d'entité..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </Form.Group>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onSelect={k => setActiveTab(k)}
        id="replacement-tabs"
        className="mb-0" // mb-0 pour coller la Card en dessous
        justify
      >
        <Tab eventKey="company" title="Participants Entreprise">
          <Card className="mt-0 border-top-0">
            {renderParticipantsTable(
              filteredCompanyParticipants,
              'company',
              'Entreprise' // typeLabel simplifié
            )}
          </Card>
        </Tab>
        <Tab eventKey="partner" title="Participants Partenaire">
          <Card className="mt-0 border-top-0">
            {renderParticipantsTable(
              filteredPartnerParticipants,
              'partner',
              'Partenaire' // typeLabel simplifié
            )}
          </Card>
        </Tab>
      </Tabs>

      {selectedParticipant && (
        <ReplaceParticipantModal
          show={showModal}
          onHide={handleModalClose}
          participant={selectedParticipant}
          participantType={participantTypeForModal}
          salonId={salon.id}
          onReplacementSuccess={handleReplacementSuccessFromModal}
        />
      )}
    </div>
  );
};

ReplacementTab.propTypes = {
  salon: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    company_participants: PropTypes.array,
    partner_participants: PropTypes.array
  }).isRequired,
  onParticipantReplaced: PropTypes.func.isRequired,
  dynamicTabContentContainerStyle: PropTypes.func.isRequired,
  isFullScreen: PropTypes.bool.isRequired
};

export default ReplacementTab;
